"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "~/lib/utils";

function Drawer({
	shouldScaleBackground = true,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
	return (
		<DrawerPrimitive.Root
			shouldScaleBackground={shouldScaleBackground}
			{...props}
		/>
	);
}
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

function DrawerOverlay({ ref: reference, className, ...props }: { ref: React.RefObject<React.ElementRef<typeof DrawerPrimitive.Overlay>> } & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>) {
	return (
		<DrawerPrimitive.Overlay
			className={cn("fixed inset-0 z-50 bg-black/80", className)}
			ref={reference}
			{...props}
		/>
	);
}
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

function DrawerContent({ ref: reference, className, children, ...props }: { ref: React.RefObject<React.ElementRef<typeof DrawerPrimitive.Content>> } & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>) {
	return (
		<DrawerPortal>
			<DrawerOverlay />
			<DrawerPrimitive.Content
				className={cn(
					"fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background",
					className
				)}
				ref={reference}
				{...props}
			>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				{children}
			</DrawerPrimitive.Content>
		</DrawerPortal>
	);
}
DrawerContent.displayName = "DrawerContent";

function DrawerHeader({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
			{...props}
		/>
	);
}
DrawerHeader.displayName = "DrawerHeader";

function DrawerFooter({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn("mt-auto flex flex-col gap-2 p-4", className)}
			{...props}
		/>
	);
}
DrawerFooter.displayName = "DrawerFooter";

function DrawerTitle({ ref: reference, className, ...props }: { ref: React.RefObject<React.ElementRef<typeof DrawerPrimitive.Title>> } & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>) {
	return (
		<DrawerPrimitive.Title
			className={cn(
				"text-lg font-semibold leading-none tracking-tight",
				className
			)}
			ref={reference}
			{...props}
		/>
	);
}
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

function DrawerDescription({ ref: reference, className, ...props }: { ref: React.RefObject<React.ElementRef<typeof DrawerPrimitive.Description>> } & React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>) {
	return (
		<DrawerPrimitive.Description
			className={cn("text-sm text-muted-foreground", className)}
			ref={reference}
			{...props}
		/>
	);
}
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	DrawerPortal,
	DrawerTitle,
	DrawerTrigger,
};
